import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = window.location.origin;
const CurrencyContext = createContext({
  rate: 1370,
  showUsd: true,
  displayMode: "both",
  refresh: () => {},
  setMode: () => {},
});

export function CurrencyProvider({ children }) {
  const { token } = useAuth();
  const [rate, setRate] = useState(1370);
  const [showUsd, setShowUsd] = useState(true);
  const [displayMode, setDisplayMode] = useState("both"); // "both" | "iqd" | "usd"

  // Apply mode class to <html> so global CSS can react
  useEffect(() => {
    const html = document.documentElement;
    html.classList.remove("currency-mode-both", "currency-mode-iqd", "currency-mode-usd");
    html.classList.add(`currency-mode-${displayMode}`);
  }, [displayMode]);

  const loadSettings = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/store-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        if (res.data.usd_exchange_rate && res.data.usd_exchange_rate > 0) {
          setRate(Number(res.data.usd_exchange_rate));
        }
        if (typeof res.data.show_usd === "boolean") {
          setShowUsd(res.data.show_usd);
        }
        if (res.data.display_mode && ["both", "iqd", "usd"].includes(res.data.display_mode)) {
          setDisplayMode(res.data.display_mode);
        } else if (res.data.show_usd === false) {
          setDisplayMode("iqd");
        }
      }
    } catch (e) {
      // silent fail - use defaults
    }
  };

  // Update mode locally + persist to server
  const setMode = async (mode) => {
    if (!["both", "iqd", "usd"].includes(mode)) return;
    setDisplayMode(mode);
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const current = await axios.get(`${API}/api/store-settings`, { headers });
      const payload = {
        ...current.data,
        store_name: current.data.store_name || "محلي",
        display_mode: mode,
        show_usd: mode !== "iqd",
      };
      delete payload.user_id;
      delete payload.updated_at;
      await axios.post(`${API}/api/store-settings`, payload, { headers });
    } catch (e) {
      // best-effort persist
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <CurrencyContext.Provider value={{ rate, showUsd, displayMode, refresh: loadSettings, setMode }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}

// Format an IQD amount as "≈ $X.XX"
export function formatUSD(amount, rate) {
  if (!rate || rate <= 0) return "";
  const usd = (Number(amount) || 0) / rate;
  return `≈ $${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
